import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CircularProgress from "@mui/material/CircularProgress";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import StatusChip from "components/StatusChip";

import useServingTeamData from "layouts/tables/data/servingTeamData";

const ASSIGNMENT_STATUS_COLOR = { assigned: "default", confirmed: "success", declined: "error" };

function ServingTeams() {
  const {
    columns,
    rows,
    isLoading,
    error,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    membersModalOpen,
    setMembersModalOpen,
    schedulesModalOpen,
    setSchedulesModalOpen,
    assignmentsModalOpen,
    setAssignmentsModalOpen,
    selectedTeam,
    selectedSchedule,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    leaderSearch,
    setLeaderSearch,
    leaderResults,
    handleSelectLeader,
    teamMembers,
    isLoadingMembers,
    memberSearch,
    setMemberSearch,
    memberResults,
    memberRoleLabel,
    setMemberRoleLabel,
    handleAddTeamMember,
    handleRemoveTeamMember,
    schedules,
    isLoadingSchedules,
    scheduleForm,
    setScheduleForm,
    isSavingSchedule,
    handleCreateSchedule,
    handleDeleteSchedule,
    handleOpenAssignments,
    assignmentSearch,
    setAssignmentSearch,
    assignmentResults,
    handleAddAssignment,
    handleRemoveAssignment,
    isSendingReminders,
    handleSendReminders,
  } = useServingTeamData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading serving teams...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Serving Teams"
        subtitle="Volunteer teams, member rosters, and service schedules with SMS reminders."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            New Team
          </Button>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search serving teams..."
        />
      </Card>

      {/* Create / Edit team */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit Serving Team" : "New Serving Team"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Create Team"}
        saveDisabled={!form.name.trim() || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            label="Team Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Leader"
              placeholder="Search member..."
              value={leaderSearch}
              onChange={(e) => {
                setLeaderSearch(e.target.value);
                setForm((prev) => ({ ...prev, leader_member_id: null }));
              }}
              fullWidth
              margin="normal"
            />
            {leaderResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {leaderResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleSelectLeader(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </FormSection>
      </FormDialog>

      {/* Members */}
      <FormDialog
        open={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
        title={`Members — ${selectedTeam?.name || ""}`}
        onSave={() => setMembersModalOpen(false)}
        saveLabel="Done"
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Add a member"
              placeholder="Search by name or phone..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              fullWidth
              margin="normal"
            />
            {memberResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {memberResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleAddTeamMember(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>
          <TextField
            label="Role (optional, e.g. Usher, Vocalist)"
            value={memberRoleLabel}
            onChange={(e) => setMemberRoleLabel(e.target.value)}
            fullWidth
            margin="normal"
          />
        </FormSection>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Current members ({teamMembers.length})
        </Typography>
        {isLoadingMembers ? (
          <CircularProgress size={24} />
        ) : teamMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No members yet.
          </Typography>
        ) : (
          teamMembers.map((m) => (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>{m.first_name?.[0]}</Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {m.first_name} {m.surname}
                  </Typography>
                  {m.pivot?.role_label && (
                    <Typography variant="caption" color="text.secondary">
                      {m.pivot.role_label}
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveTeamMember(m.id)}>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          ))
        )}
      </FormDialog>

      {/* Rosters / Schedules */}
      <FormDialog
        open={schedulesModalOpen}
        onClose={() => setSchedulesModalOpen(false)}
        title={`Roster Schedules — ${selectedTeam?.name || ""}`}
        onSave={() => setSchedulesModalOpen(false)}
        saveLabel="Done"
        maxWidth="sm"
      >
        <FormSection>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            New schedule
          </Typography>
          <TextField
            label="Title"
            value={scheduleForm.title}
            onChange={(e) => setScheduleForm({ ...scheduleForm, title: e.target.value })}
            fullWidth
            margin="normal"
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Service Date"
              type="date"
              value={scheduleForm.service_date}
              onChange={(e) => setScheduleForm({ ...scheduleForm, service_date: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Service Time"
              type="time"
              value={scheduleForm.service_time}
              onChange={(e) => setScheduleForm({ ...scheduleForm, service_time: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Notes"
            value={scheduleForm.notes}
            onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateSchedule}
            disabled={isSavingSchedule || !scheduleForm.title.trim()}
            sx={{ mt: 1 }}
          >
            {isSavingSchedule ? "Saving..." : "Add Schedule"}
          </Button>
        </FormSection>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Upcoming &amp; past schedules ({schedules.length})
        </Typography>
        {isLoadingSchedules ? (
          <CircularProgress size={24} />
        ) : schedules.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No roster schedules yet.
          </Typography>
        ) : (
          schedules.map((s) => (
            <Box
              key={s.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {s.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(s.service_date).toLocaleDateString()} {s.service_time || ""} ·{" "}
                  {(s.assignments || []).length} assigned
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button size="small" variant="outlined" onClick={() => handleOpenAssignments(s)}>
                  Manage
                </Button>
                <IconButton size="small" onClick={() => handleDeleteSchedule(s.id)}>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </FormDialog>

      {/* Assignments */}
      <FormDialog
        open={assignmentsModalOpen}
        onClose={() => setAssignmentsModalOpen(false)}
        title={`Assignments — ${selectedSchedule?.title || ""}`}
        onSave={() => setAssignmentsModalOpen(false)}
        saveLabel="Done"
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Assign a member"
              placeholder="Search by name or phone..."
              value={assignmentSearch}
              onChange={(e) => setAssignmentSearch(e.target.value)}
              fullWidth
              margin="normal"
            />
            {assignmentResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {assignmentResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleAddAssignment(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>
          <Button
            variant="contained"
            color="info"
            startIcon={<NotificationsActiveOutlinedIcon />}
            onClick={handleSendReminders}
            disabled={isSendingReminders || (selectedSchedule?.assignments || []).length === 0}
            sx={{ mt: 1 }}
            fullWidth
          >
            {isSendingReminders ? "Sending..." : "Send SMS Reminders to All Assignees"}
          </Button>
        </FormSection>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Assigned ({(selectedSchedule?.assignments || []).length})
        </Typography>
        {(selectedSchedule?.assignments || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No one assigned yet.
          </Typography>
        ) : (
          (selectedSchedule?.assignments || []).map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>
                  {a.member?.first_name?.[0]}
                </Avatar>
                <Typography variant="body2" fontWeight={600}>
                  {a.member ? `${a.member.first_name} ${a.member.surname}` : "—"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StatusChip value={a.status} color={ASSIGNMENT_STATUS_COLOR[a.status]} />
                <IconButton size="small" onClick={() => handleRemoveAssignment(a.id)}>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedTeam?.name}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default ServingTeams;
